import { Check, ChevronsUpDown, X } from 'lucide-react'
import * as React from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export type MultiSelectOption = {
  value: string
  label: string
  description?: string
  searchValue?: string
  disabled?: boolean
}

export type MultiSelectGroup = {
  label?: string
  options: MultiSelectOption[]
}

type MultiSelectProps = {
  options?: MultiSelectOption[]
  groups?: MultiSelectGroup[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  triggerClassName?: string
  testId?: string
}

export function MultiSelect({
  options,
  groups,
  value,
  onValueChange,
  placeholder = 'Select options',
  searchPlaceholder = 'Search...',
  emptyText = 'No options found',
  disabled = false,
  className,
  triggerClassName,
  testId,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const optionGroups = groups ?? [{ options: options ?? [] }]
  const flatOptions = optionGroups.flatMap(group => group.options)
  const selectedOptions = flatOptions.filter(option => value.includes(option.value))

  function handleOptionChange(optionValue: string, checked: boolean) {
    if (checked) {
      onValueChange(Array.from(new Set([...value, optionValue])))
      return
    }

    onValueChange(value.filter(currentValue => currentValue !== optionValue))
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid={testId}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'min-h-9 h-auto w-full justify-between gap-2 bg-secondary/50 px-3 py-1.5 text-xs font-normal',
            triggerClassName,
          )}
        >
          <span className="flex min-w-0 flex-1 flex-wrap gap-1">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map(option => (
                <Badge key={option.value} variant="default" className="max-w-full gap-1 px-1.5 py-0 text-[11px]">
                  <span className="truncate">{option.label}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Remove ${option.label}`}
                    className="ml-0.5 inline-flex size-3 shrink-0 items-center justify-center rounded-sm hover:bg-primary-foreground/20"
                    onMouseDown={event => {
                      event.preventDefault()
                      event.stopPropagation()
                    }}
                    onClick={event => {
                      event.preventDefault()
                      event.stopPropagation()
                      handleOptionChange(option.value, false)
                    }}
                  >
                    <X className="size-2.5 text-current" />
                  </span>
                </Badge>
              ))
            )}
          </span>
          <ChevronsUpDown className="ml-2 size-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        usePortal={false}
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', className)}
        align="start"
      >
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="text-xs" />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            {optionGroups.map((group, groupIndex) =>
              group.options.length > 0 ? (
                <CommandGroup key={group.label ?? groupIndex} heading={group.label}>
                  {group.options.map(option => {
                    const checked = value.includes(option.value)

                    return (
                      <CommandItem
                        key={option.value}
                        data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                        data-state={checked ? 'checked' : 'unchecked'}
                        value={option.searchValue ?? `${option.label} ${option.description ?? ''}`}
                        onSelect={() => {
                          if (option.disabled) return
                          handleOptionChange(option.value, !checked)
                        }}
                        className={cn('items-start text-xs', option.disabled && 'cursor-not-allowed opacity-50')}
                      >
                        <Checkbox
                          checked={checked}
                          disabled={option.disabled}
                          className="pointer-events-none mt-0.5 data-[state=checked]:text-white"
                        />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-medium text-foreground">{option.label}</span>
                          {option.description ? (
                            <span className="truncate text-[11px] text-muted-foreground">{option.description}</span>
                          ) : null}
                        </span>
                        {checked ? <Check className="ml-auto size-3 text-primary" /> : null}
                      </CommandItem>
                    )
                  })}
                </CommandGroup>
              ) : null,
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
